import React, { useState, useEffect } from "react";
import BackButton from "./BackButton";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../config";
import './MobileUpdateModal.css'; // Import the CSS file
import InitialFeedbackModal from "./InitialFeedbackModal"; // Import InitialFeedbackModal
import feedbackService from "../services/feedbackService";

const MobileUpdateModal = ({ isOpen, onClose }) => {
  const { encryptedData } = useAuth(); // Use encrypted data from AuthContext
  const [currentMobile, setCurrentMobile] = useState("No current mobile number");
  const [newMobile, setNewMobile] = useState("");
  const [isInitialFeedbackModalOpen, setIsInitialFeedbackModalOpen] = useState(false); // State for InitialFeedbackModal
  const [loading, setLoading] = useState(false); // Add loading state

  useEffect(() => {
    if (isOpen) {
      fetchCurrentMobileNumber();
    }
  }, [isOpen]);

  const fetchCurrentMobileNumber = async () => {
    setLoading(true); // Start loading
    try {
      const payload = {
        data: encryptedData,
      };

      const response = await axios.post(`${API_BASE_URL}/x1bf/x1wh/member/members-credentials`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authentication/token")}` },
      });

      if (response.data.status === "success") {
        setCurrentMobile(response.data.data.mobile || "No current mobile number");
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch current mobile number.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleUpdateMobileNumber = async () => {
    const philMobileRegex = /^(\+639\d{9}|09\d{9})$/;

    if (!newMobile || !philMobileRegex.test(newMobile)) {
      Swal.fire({
        title: "Invalid New Mobile Number",
        text: "Please enter a valid new mobile number in the format +639XXXXXXXXX or 09XXXXXXXXX.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update your mobile number?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update it",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      setLoading(true); // Start loading
      try {
        const payload = {
          data: encryptedData,
          mobile: newMobile, // Send the new mobile number
        };

        const response = await axios.post(`${API_BASE_URL}/x1bf/x1wh/member/members-credentials-update-mobile`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authentication/token")}` },
        });

        handleResponse(response.data);
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          title: "Oops!",
          text: "Something went wrong. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const handleResponse = (data) => {
    if (data.status === "error") {
      Swal.fire({
        title: "Oops!",
        text: data.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } else if (data.status === "success") {
      Swal.fire({
        title: "Success!",
        text: data.message,
        icon: "success",
        confirmButtonText: "OK",
      }).then(async () => {
        const hasSubmitted = await feedbackService.hasSubmittedFeedback(encryptedData);
        if (!hasSubmitted) {
          setIsInitialFeedbackModalOpen(true); // Open InitialFeedbackModal
        }
        setNewMobile("");
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-backrow"><BackButton onClick={onClose} /></div>
          <h2>Update Mobile Number</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              <p>
                <strong>Current Mobile Number:</strong> {currentMobile}
              </p>
              <div className="input-group">
                <label htmlFor="new-mobile-number">Enter your NEW Mobile Number:</label>
                <input
                  type="text"
                  id="new-mobile-number"
                  value={newMobile}
                  onChange={(e) => setNewMobile(e.target.value)}
                  placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
                  disabled={loading} // Disable input during loading
                />
                <p className="input-help-text">
                  Enter a valid new mobile number in the format +639XXXXXXXXX or 09XXXXXXXXX.
                </p>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="update-button" onClick={handleUpdateMobileNumber} disabled={loading}>Update</button>
        </div>
      </div>

      {/* Render InitialFeedbackModal */}
      <InitialFeedbackModal
        isOpen={isInitialFeedbackModalOpen}
        onClose={() => setIsInitialFeedbackModalOpen(false)}
      />
    </div>
  );
};

export default MobileUpdateModal;